/**
 * FLASK SYSTEM — Matter.js engine for flask, spawn/merge logic, rendering.
 * One underlying flask state; used by RUN view (big flask panel) and Stage II (centered flask).
 */

import Matter from "matter-js";
import * as C from "../config/constants.js";
import { DEFAULT_BODY_RADIUS, DEFAULT_WALL_THICKNESS } from "./system/constants.js";
import {
  addBodyAtForSystem,
  clearBodiesForSystem,
  createBodyForSystem,
  dropIntoFlaskForSystem,
  mergeFlaskStaticMatterOptions,
  spawnTestBodiesForSystem,
} from "./system/bodies.js";
import { attachCollisionHandlers } from "./system/collisions.js";
import {
  buildFlaskGeometry,
  getFlaskGravityForBounds,
  getFlaskPanelScale,
} from "./system/geometry.js";
import { createFlaskWalls, createMouthSealBodies } from "./system/walls.js";
import { drawSystem } from "./system/render.js";
import {
  enforceMouthContainment,
  enforceWallContainment,
  getMouthGateTopY,
  requestContainmentBurst,
  shouldIgnoreMouthGateCollision,
} from "./system/containment.js";
import { updateSystem } from "./system/update.js";
import { minimizeBodyForSystem, pickBodyAtPanelPoint } from "./system/minimize.js";
import { getOverflowAlertY, getOverflowWarningY, getOverflowCheckY, getOverflowNearFullY } from "./system/overflow.js";

/**
 * FlaskSystem owns: Matter engine/world, flask walls, body list, collision merge, draw.
 * @param {Object} opts - { evolutionChain, getUnlockTier(tierId), onUnlockTier(tierId) }
 * @param {Object} bounds - { left, right, top, bottom } in the coordinate system where flask is drawn
 */
function FlaskSystem(opts, bounds) {
  this.opts = opts || {};
  this.bounds = bounds || { left: 20, right: 300, top: 70, bottom: 465 };
  this.bodyRadiusByTier = this.opts.bodyRadiusByTier || DEFAULT_BODY_RADIUS;
  this.flaskWallThickness =
    this.opts.flaskWallThickness != null ? this.opts.flaskWallThickness : DEFAULT_WALL_THICKNESS;
  this.mergeFlashRadius = this.opts.mergeFlashRadius != null ? this.opts.mergeFlashRadius : 25;
  this.engine = null;
  this.world = null;
  this.bodies = [];
  this.bodiesMap = {};
  this.mergeFlashUntil = 0;
  this.mergeFlashPos = null;
  this.mergeParticles = [];
  this.mergeParticlesStart = 0;
  this.mergeParticlesUntil = 0;
  this.dropSplashUntil = 0;
  this.dropSplashParticles = [];
  this.lastImpactSoundAt = 0;
  this.evolutionChain = this.opts.evolutionChain || [];
  this.runConfig = this.opts.runConfig || C.RUN_CONFIG || {};
  this.physicsWallExtendUpFrac = this.opts.physicsWallExtendUpFrac != null
    ? Number(this.opts.physicsWallExtendUpFrac)
    : 0;
  if (!isFinite(this.physicsWallExtendUpFrac) || this.physicsWallExtendUpFrac < 0) {
    this.physicsWallExtendUpFrac = 0;
  }
  this.dropSpawnY = this.bounds.top - (this.bounds.bottom - this.bounds.top) * 0.07;
  this.flaskWalls = [];
  this.flaskEnabled = true;
  this.mouthSealBody = null;
  this.mouthSealBodies = [];
  this.mouthSealed = true;
  this.activeMergeMotions = [];
  this.mergeMotionDraw = null;
  this.activeShrinkMotions = [];
  this.shrinkMotionDraw = null;
}

FlaskSystem.prototype.syncEngineGravity = function () {
  if (!this.engine) return;
  var gravity = getFlaskGravityForBounds(this.bounds, this.runConfig);
  if (!isFinite(gravity) || gravity <= 0) gravity = C.FLASK_GRAVITY != null ? Number(C.FLASK_GRAVITY) : 0.8;
  this.engine.gravity.y = gravity;
};

FlaskSystem.prototype.ensureEngine = function () {
  if (this.engine) {
    this.syncEngineGravity();
    return;
  }
  var Engine = Matter.Engine;
  var World = Matter.World;
  var Bodies = Matter.Bodies;
  var rc = this.runConfig || {};
  var positionIterations = (rc.FLASK_PHYSICS_POSITION_ITERATIONS != null)
    ? Number(rc.FLASK_PHYSICS_POSITION_ITERATIONS)
    : (C.FLASK_PHYSICS_POSITION_ITERATIONS != null
      ? Number(C.FLASK_PHYSICS_POSITION_ITERATIONS)
      : 14);
  var velocityIterations = (rc.FLASK_PHYSICS_VELOCITY_ITERATIONS != null)
    ? Number(rc.FLASK_PHYSICS_VELOCITY_ITERATIONS)
    : (C.FLASK_PHYSICS_VELOCITY_ITERATIONS != null
      ? Number(C.FLASK_PHYSICS_VELOCITY_ITERATIONS)
      : 10);
  var constraintIterations = (rc.FLASK_PHYSICS_CONSTRAINT_ITERATIONS != null)
    ? Number(rc.FLASK_PHYSICS_CONSTRAINT_ITERATIONS)
    : (C.FLASK_PHYSICS_CONSTRAINT_ITERATIONS != null
      ? Number(C.FLASK_PHYSICS_CONSTRAINT_ITERATIONS)
      : 10);
  var gravity = getFlaskGravityForBounds(this.bounds, this.runConfig);
  if (!isFinite(gravity) || gravity <= 0) gravity = C.FLASK_GRAVITY != null ? Number(C.FLASK_GRAVITY) : 0.8;
  this.engine = Engine.create({
    gravity: { x: 0, y: gravity },
    positionIterations: positionIterations,
    velocityIterations: velocityIterations,
    constraintIterations: constraintIterations,
  });
  this.world = this.engine.world;

  var geom = buildFlaskGeometry(this.bounds, this.flaskWallThickness, {
    physicsWallExtendUpFrac: this.physicsWallExtendUpFrac,
  });
  this._flaskInterior = geom.interior;
  this._flaskGeometry = geom.geometry;
  this.dropSpawnY = geom.dropSpawnY;

  var wallOptions = mergeFlaskStaticMatterOptions();
  this.flaskWalls = createFlaskWalls(Bodies, this._flaskGeometry, wallOptions);
  this._flaskWallIds = {};
  for (var wi = 0; wi < this.flaskWalls.length; wi++) {
    this._flaskWallIds[this.flaskWalls[wi].id] = true;
  }
  if (this.flaskEnabled) {
    World.add(this.world, this.flaskWalls);
  }

  this.mouthSealBodies = createMouthSealBodies(Bodies, this._flaskGeometry, wallOptions);
  this.mouthSealBody = this.mouthSealBodies.length ? this.mouthSealBodies[0] : null;
  if (this.flaskEnabled && this.mouthSealed && this.mouthSealBodies.length) {
    World.add(this.world, this.mouthSealBodies);
  }

  attachCollisionHandlers(this);
};

FlaskSystem.prototype.createBody = function (tier, x, y) {
  return createBodyForSystem(this, tier, x, y);
};

FlaskSystem.prototype.dropIntoFlask = function (tier, screenX, fallPanelWidth, fallMouthCenterX, fallMouthWidth) {
  return dropIntoFlaskForSystem(this, tier, screenX, fallPanelWidth, fallMouthCenterX, fallMouthWidth);
};

FlaskSystem.prototype.getBodyCount = function () {
  return this.bodies.length;
};

FlaskSystem.prototype.addBodyAt = function (tier, x, y, opts) {
  return addBodyAtForSystem(this, tier, x, y, opts);
};

FlaskSystem.prototype.clearBodies = function () {
  return clearBodiesForSystem(this);
};

FlaskSystem.prototype.spawnTestBodies = function (maxCount) {
  return spawnTestBodiesForSystem(this, maxCount);
};

FlaskSystem.prototype.update = function (deltaMs) {
  return updateSystem(this, deltaMs);
};

FlaskSystem.prototype._getMouthGateTopY = function () {
  return getMouthGateTopY(this);
};

FlaskSystem.prototype._shouldIgnoreMouthGateCollision = function (pair) {
  return shouldIgnoreMouthGateCollision(this, pair);
};

FlaskSystem.prototype._enforceMouthContainment = function () {
  return enforceMouthContainment(this);
};

FlaskSystem.prototype._enforceWallContainment = function () {
  return enforceWallContainment(this);
};

FlaskSystem.prototype.getOverflowAlertY = function () {
  return getOverflowAlertY(this);
};

FlaskSystem.prototype.getOverflowWarningY = function () {
  return getOverflowWarningY(this);
};

FlaskSystem.prototype.getOverflowCheckY = function () {
  return getOverflowCheckY(this);
};

FlaskSystem.prototype.getOverflowNearFullY = function () {
  return getOverflowNearFullY(this);
};

FlaskSystem.prototype.setMouthSealed = function (sealed) {
  var next = !!sealed;
  if (next === this.mouthSealed) return;
  this.mouthSealed = next;
  this.ensureEngine();
  if (!this.world || !this.flaskEnabled) return;
  var seals = this.mouthSealBodies && this.mouthSealBodies.length
    ? this.mouthSealBodies
    : (this.mouthSealBody ? [this.mouthSealBody] : []);
  if (!seals.length) return;
  if (next) {
    Matter.World.add(this.world, seals);
  } else {
    Matter.World.remove(this.world, seals);
  }
};

FlaskSystem.prototype.shatterFlask = function () {
  this.flaskEnabled = false;
  if (!this.world) return;
  if (this.mouthSealBodies && this.mouthSealBodies.length) {
    Matter.World.remove(this.world, this.mouthSealBodies);
  } else if (this.mouthSealBody) {
    Matter.World.remove(this.world, this.mouthSealBody);
  }
  if (!this.flaskWalls || this.flaskWalls.length === 0) return;
  for (var i = 0; i < this.flaskWalls.length; i++) {
    Matter.World.remove(this.world, this.flaskWalls[i]);
  }
};

/**
 * Draw flask and all bodies. Delegates to FlaskRenderer.
 * Call with ctx already translated to flask panel origin if needed.
 */
FlaskSystem.prototype.draw = function (ctx, renderOpts) {
  return drawSystem(this, ctx, renderOpts);
};

/**
 * Apply random impulses to all bodies (shake).
 * Sets velocity immediately so the speed cap actually runs (Matter applyForce
 * does not change velocity until the next Engine.update).
 * @returns {number} the rolled intensity multiplier (0.85, 1.55 or 2.7)
 */
FlaskSystem.prototype.applyShake = function (strength) {
  // Roll a random intensity tier each shake click — adds excitement and unpredictability.
  var roll = Math.random();
  var multiplier;
  if (roll < 0.45) {
    // Moderate (45%) — clear lift, still controllable
    multiplier = 0.85;
  } else if (roll < 0.82) {
    // Strong (37%) — high toss
    multiplier = 1.55;
  } else {
    // Heavy (18%) — big pop
    multiplier = 2.7;
  }

  var panelScale = getFlaskPanelScale(this.bounds);
  if (!isFinite(panelScale) || panelScale <= 0) panelScale = 1;
  var spreadDeg = C.SHAKE_IMPULSE_ANGLE_SPREAD_DEG != null ? Number(C.SHAKE_IMPULSE_ANGLE_SPREAD_DEG) : 55;
  var spreadRad = (Math.max(0, spreadDeg) * Math.PI) / 180;
  var baseAngle = -Math.PI / 2;
  var jitterFrac = C.SHAKE_KICK_JITTER_FRAC != null ? Number(C.SHAKE_KICK_JITTER_FRAC) : 0.22;
  if (!isFinite(jitterFrac) || jitterFrac < 0) jitterFrac = 0.22;
  var largeSpreadFrac = C.SHAKE_LARGE_TIER_SPREAD_FRAC != null ? Number(C.SHAKE_LARGE_TIER_SPREAD_FRAC) : 0.55;
  if (!isFinite(largeSpreadFrac) || largeSpreadFrac < 0) largeSpreadFrac = 0.55;
  largeSpreadFrac = Math.min(1, largeSpreadFrac);

  var maxTierIdx = 1;
  if (this.evolutionChain && this.evolutionChain.length > 0) {
    maxTierIdx = Math.max(1, this.evolutionChain.length - 1);
  } else if (this.bodyRadiusByTier && this.bodyRadiusByTier.length > 0) {
    maxTierIdx = Math.max(1, this.bodyRadiusByTier.length - 1);
  }

  requestContainmentBurst(this);
  for (var i = 0; i < this.bodies.length; i++) {
    var meta = this.bodies[i];
    var body = meta.body;
    var sizeTier = meta.physicsSizeTier != null ? meta.physicsSizeTier
      : (meta.tier != null ? meta.tier : 0);
    var eased = C.getShakeTierEase(sizeTier, maxTierIdx);
    var tierMagMul = C.getShakeTierImpulseMul(sizeTier, maxTierIdx);
    var jitter = 1 + (Math.random() - 0.5) * 2 * jitterFrac;
    if (!(jitter > 0)) jitter = 1;
    var mag = C.getShakeKickSpeed(strength, multiplier, tierMagMul, panelScale) * jitter;
    var maxSpeed = mag;
    var spreadScale = 1 - (1 - largeSpreadFrac) * eased;
    var angle = baseAngle + (Math.random() - 0.5) * 2 * spreadRad * spreadScale;
    var vx = (body.velocity && body.velocity.x) || 0;
    var vy = (body.velocity && body.velocity.y) || 0;
    var nx = vx + Math.cos(angle) * mag;
    var ny = vy + Math.sin(angle) * mag;
    var speed = Math.sqrt(nx * nx + ny * ny);
    if (speed > maxSpeed && speed > 0) {
      var scale = maxSpeed / speed;
      nx *= scale;
      ny *= scale;
    }
    Matter.Body.setVelocity(body, { x: nx, y: ny });
  }
  // The rolled intensity, so presentation (jar wobble, bubbles) can match how hard it was.
  return multiplier;
};

/**
 * Remove up to `count` bodies, chosen randomly from the 3 lowest tiers present.
 */
FlaskSystem.prototype.removeLowestTierBodies = function (count) {
  if (this.bodies.length === 0) return;
  var tiers = [];
  for (var i = 0; i < this.bodies.length; i++) {
    var t = this.bodies[i].tier;
    if (tiers.indexOf(t) === -1) tiers.push(t);
  }
  tiers.sort(function (a, b) {
    return a - b;
  });
  var lowest3 = tiers.slice(0, 3);
  var candidates = this.bodies.filter(function (m) {
    return lowest3.indexOf(m.tier) !== -1;
  });
  var toRemove = [];
  while (toRemove.length < count && candidates.length > 0) {
    var idx = Math.floor(Math.random() * candidates.length);
    toRemove.push(candidates.splice(idx, 1)[0]);
  }
  var World = Matter.World;
  for (var j = 0; j < toRemove.length; j++) {
    var meta = toRemove[j];
    World.remove(this.world, meta.body);
    delete this.bodiesMap[meta.body.id];
  }
  this.bodies = this.bodies.filter(function (m) {
    return toRemove.indexOf(m) === -1;
  });
  requestContainmentBurst(this);
};

FlaskSystem.prototype.pickBodyAtPanelPoint = function (panelX, panelY) {
  return pickBodyAtPanelPoint(this, panelX, panelY);
};

FlaskSystem.prototype.minimizeBody = function (meta) {
  return minimizeBodyForSystem(this, meta);
};

FlaskSystem.prototype.removeBodiesByTiers = function (tiers) {
  if (!tiers || tiers.length === 0 || this.bodies.length === 0) return 0;
  var tierSet = {};
  for (var i = 0; i < tiers.length; i++) tierSet[tiers[i]] = true;
  var toRemove = this.bodies.filter(function (m) {
    return !!tierSet[m.tier];
  });
  if (toRemove.length === 0) return 0;
  var World = Matter.World;
  for (var j = 0; j < toRemove.length; j++) {
    var meta = toRemove[j];
    World.remove(this.world, meta.body);
    delete this.bodiesMap[meta.body.id];
  }
  this.bodies = this.bodies.filter(function (m) {
    return toRemove.indexOf(m) === -1;
  });
  requestContainmentBurst(this);
  return toRemove.length;
};

export { FlaskSystem };
export default FlaskSystem;
